import { Suspense, lazy } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from './store';
import {
  createBrowserRouter,
  Outlet,
  redirect,
  RouterProvider,
  useRouteError,
} from 'react-router-dom';
import Header from './components/Header/Header';
import CatalogManager from './components/CatalogManager/CatalogManager';
import { usePageTracking } from './hooks/usePageTracking';
import { authService } from './utils/auth0/authService';
import {
  ROUTE_AUTOCATEGORIZED,
  ROUTE_CATEGORY_STATISTICS,
  ROUTE_GRAPH,
  ROUTE_HOME,
  ROUTE_FILTERS,
  ROUTE_HFB,
  ROUTE_IMAGE_OVERRIDES,
  ROUTE_ITEM_GROUPING,
  ROUTE_NVFS,
  ROUTE_UNHIDDEN,
  ROUTE_MARKET_REQUEST,
  ROUTE_SHOP_DASHBOARD,
} from './routes';
const Toaster = lazy(() => import('./components/Toaster'));
const ModalManager = lazy(() => import('./components/ModalManager'));
import { selectAppIsLoading } from './store/slices/app';
import { selectLandingPage } from './store/selectors/roles/roles';

const Root = () => {
  usePageTracking();
  return (
    <>
      <Header />
      <Outlet />
      <Suspense>
        <ModalManager />
        <Toaster />
      </Suspense>
    </>
  );
};

const BubbleError = () => {
  /* rethrow to react error boundary */
  const error = useRouteError();
  throw error;
};

export const Router = () => {
  const appLoading = useSelector(selectAppIsLoading);
  const marketCode = useSelector((state: RootState) => state.intl.marketCode);
  const landingPage = useSelector(selectLandingPage);
  const landingPath =
    landingPage === 'indexable' ? `/${marketCode}/filters` : `/${marketCode}?`;

  if (!authService.getClient() || appLoading) return null;

  const routes = createBrowserRouter([
    {
      path: '/',
      element: <Root />,
      errorElement: <BubbleError />,
      hydrateFallbackElement: <></>,
      children: [
        {
          index: true,
          element: <CatalogManager />,
          loader: async () => {
            if (marketCode) {
              return redirect(landingPath);
            }
            return true;
          },
        },
        {
          path: ROUTE_HOME,
          element: <CatalogManager />,
        },
        {
          path: ROUTE_FILTERS,
          children: [
            {
              path: `${ROUTE_FILTERS}/:catalogId/*`,
              lazy: async () => {
                const { IndexableFilters } =
                  await import('./components/IndexableFilters/IndexableFilters');
                return { Component: IndexableFilters };
              },
            },
          ],
          lazy: async () => {
            const { IndexableFilters } =
              await import('./components/IndexableFilters/IndexableFilters');
            return { Component: IndexableFilters };
          },
        },
        {
          path: ROUTE_AUTOCATEGORIZED,
          lazy: async () => {
            const { AutoCategorized } =
              await import('./components/Reports/AutoCategorized/AutoCategorized');
            return { Component: AutoCategorized };
          },
        },
        {
          path: ROUTE_UNHIDDEN,
          lazy: async () => {
            const { UnHidden } =
              await import('./components/Reports/UnHidden/UnHidden');
            return { Component: UnHidden };
          },
        },
        {
          path: ROUTE_ITEM_GROUPING,
          lazy: async () => {
            const { ItemGrouping } =
              await import('./components/Reports/ItemGrouping/ItemGrouping');
            return { Component: ItemGrouping };
          },
        },
        {
          path: ROUTE_IMAGE_OVERRIDES,
          lazy: async () => {
            const { ImageOverrides } =
              await import('./components/Reports/ImageOverrides/ImageOverrides');

            return { Component: ImageOverrides };
          },
        },
        {
          path: ROUTE_NVFS,
          lazy: async () => {
            const { NotValidForStructureItemReports } =
              await import('./components/Reports/NotValidForStructureItemReports/NotValidForStructureItemReports');
            return { Component: NotValidForStructureItemReports };
          },
        },
        {
          path: ROUTE_GRAPH,
          lazy: async () => {
            const { CategoryGraph } =
              await import('./components/CategoryGraph/CategoryGraph');
            return { Component: CategoryGraph };
          },
        },
        {
          path: ROUTE_HFB,
          lazy: async () => {
            const { HfbReport } = await import('./components/Reports/Hfb/HfbReport');
            return { Component: HfbReport };
          },
        },
        {
          path: ROUTE_CATEGORY_STATISTICS,
          lazy: async () => {
            const { CategoryStatistics } =
              await import('./components/Reports/CategoryStatistics/CategoryStatistics');
            return { Component: CategoryStatistics };
          },
        },
        {
          path: ROUTE_MARKET_REQUEST,
          lazy: async () => {
            const { MarketRequest } =
              await import('./components/MarketRequest/MarketRequest');
            return { Component: MarketRequest };
          },
        },
        {
          path: ROUTE_SHOP_DASHBOARD,
          lazy: async () => {
            const { default: ShopDashboard } =
              await import('./components/ShopDashboard/ShopDashboard');
            return { Component: ShopDashboard };
          },
        },
        {
          path: '*',
          loader: async () => {
            if (marketCode) {
              return redirect(landingPath);
            }
            return true;
          },
        },
      ],
    },
  ]);

  return <RouterProvider router={routes} />;
};
